import { useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown } from 'components';
import { ViewStateType } from 'components/ProductTemplates/types';
import { AppContext } from 'context/ContextProvider';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PORTAL_DESCRIPTIONS } from 'types/commonTypes';
import { NavigationPath } from 'types/DomainTypes';
import { buildDisplayText } from 'utils';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { useFeeModelInfo } from 'views/shared/hooks/useFeeModelInfo';
import { StatusTypes, useHandleBuy } from 'views/shared/hooks/useHandleBuy';
import { BuysType } from 'views/shared/services/executeBuy';
import RequestWineOnFailedPurchase from 'views/shared/SlideOutViews/RequestWineOnFailedPurchase';
import SelectPortfolioView from 'views/shared/SlideOutViews/SelectPortfolioView';
import TopUpView from 'views/shared/SlideOutViews/TopUpView';
import { QUERY_REFECH_TIMEOUT } from 'views/Trade/constants';
import { generateQuantityDropdown } from 'views/Trade/helpers';
import { useFetch } from 'views/Trade/hooks/useFetch';
import { fetchOffers } from 'views/Trade/services/fetchOffers';
import {
  DisplayTextKeys,
  OffersType,
  QUERY_KEYS,
  SetSlideoutConfigType,
  SlideoutConfigType,
  TradePurchaseRefType,
  onTradeTopUpEnum,
} from 'views/Trade/types';
import WineInfoTemplate from './WineInfoTemplate';

interface ICreateOfferSlideOutProps {
  slideOutConfig: SlideoutConfigType;
  setSlideOutConfig: SetSlideoutConfigType;
}

const PurchaseView = ({ setSlideOutConfig, slideOutConfig }: ICreateOfferSlideOutProps) => {
  const {
    state: {
      settings: { accountInfo },
    },
    formatter,
    gbpToTargetCurrency,
  } = useContext(AppContext);

  const nav = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'trade:common', t), [t]);
  const fxRate = accountInfo?.account?.fxRate || 1;
  const prevAcctBalance = accountInfo?.account.totalCashOnAccount;
  const { handlePurchase: executeBuy, mutateStatus: status, resetStatus } = useHandleBuy();
  const [quantitySelected, setQuantitySelected] = useState<number>(0);
  const [topupAmount, setTopAmount] = useState(0);
  const [purchaseInfo, setPurchaseInfo] = useState<TradePurchaseRefType>();
  const [extraConfig, setExtraConfig] = useState<SlideoutConfigType['extraConfig']>();

  const { isLoading: isLoadingMarketOffers, responce: marketOffers } = useFetch({
    refetchOnWindowFocus: false,
    shouldCache: false,
    queryFn: () => fetchOffers({ searchText: slideOutConfig.offer?.lwin18, page: 0, pageSize: 50 }),
    queryKey: [QUERY_KEYS.FETCH_OFFERS_SEARCH, slideOutConfig.offer?.lwin18 || ''],
  });

  useEffect(() => {
    if (slideOutConfig?.extraConfig) {
      const config = { ...slideOutConfig.extraConfig };
      setExtraConfig(config);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const feeModelInfo = useFeeModelInfo({
    price: slideOutConfig.offer?.priceInTargetCurrency || 0,
    qty: quantitySelected,
  });

  const purchaseInfoRef = useMemo(() => {
    return encodeURIComponent(btoa(JSON.stringify(purchaseInfo)));
  }, [purchaseInfo]);

  const availableMarketOffers = useMemo(() => {
    const lowestOfferGbp = slideOutConfig.offer?.lowestOffer?.price ?? 0;
    // const test = (slideOutConfig.marketOffers || []).filter((x) => x.lwin18 === slideOutConfig.offer?.lwin18);
    // console.log('test ', test);
    const allSelectedLwin18 = (marketOffers?.data || []).filter(
      (x) => (x.price || 0).toFixed(2) === lowestOfferGbp.toFixed(2),
    );
    // console.log('allSelectedLwin18 ', allSelectedLwin18);
    allSelectedLwin18.sort((a, b) => a.quantityOutstanding - b.quantityOutstanding);

    return {
      offers: allSelectedLwin18,
      quantity: allSelectedLwin18.reduce((prev, current) => prev + current.quantityOutstanding, 0),
      lowestOfferGbp,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketOffers?.data]);

  const generateBody = (config: SlideoutConfigType, quantity: number, offers: OffersType[]) => {
    const buys: BuysType[] = [];

    let targetedQty = quantity;
    offers.forEach((x) => {
      const { lwin18, id, price: priceGbp, quantityOutstanding } = x;
      if (targetedQty === 0) return;
      if (quantityOutstanding <= targetedQty && quantityOutstanding > 0) {
        targetedQty -= quantityOutstanding;
        buys.push({
          lwin18,
          offerId: id,
          price: priceGbp,
          quantity: quantityOutstanding,
        });
      } else if (quantityOutstanding - targetedQty >= 0 && quantityOutstanding > 0) {
        buys.push({
          lwin18,
          offerId: id,
          price: priceGbp,
          quantity: targetedQty,
        });
      }
    });

    // buys.reduce((accumulator, currentValue) => accumulator + currentValue.quantity, 0);
    // console.log('buys  ', buys);

    const params: TradePurchaseRefType = {
      name: config.offer?.wineName,
      subtitle: `${config?.offer?.vintage} @ ${formatter.format(config?.offer?.priceGbp || 0)} per ${
        config.offer?.unitSize
      }`,
      priceInTargetCurrency: config.offer?.priceInTargetCurrency,
      prevAcctBalance: prevAcctBalance,
      body: {
        portfolioId: Number(feeModelInfo.selectedPortfolio?.value) || 0,
        description: slideOutConfig?.portalDescriptions ?? PORTAL_DESCRIPTIONS.PORTAL_BUY_TRADE,
        buys,
      },
    };
    return params;
  };

  const configBackButton = (nextView: ViewStateType) => {
    if (nextView === ViewStateType.TOP_UP && feeModelInfo.ddlOptions.length === 1) {
      setSlideOutConfig((prev) => ({
        ...prev,
        showBackButton: true,
        wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: false },
        innerViewState: ViewStateType.TOP_UP,
        onBack: () => {
          setSlideOutConfig((previous) => ({
            ...previous,
            showBackButton: !!extraConfig?.initOnBack,
            wineInfoTemplateConfig: { ...previous.wineInfoTemplateConfig, showTemplate: true },
            innerViewState: ViewStateType.DEFAULT,
            onBack: () => {
              if (extraConfig?.initOnBack) return extraConfig?.initOnBack?.();
            },
          }));
        },
      }));

      return;
    }

    if (nextView === ViewStateType.TOP_UP && feeModelInfo.ddlOptions.length > 1) {
      setSlideOutConfig((prev) => ({
        ...prev,
        showBackButton: true,
        wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: false },
        innerViewState: ViewStateType.TOP_UP,
        onBack: () => {
          setSlideOutConfig((previous) => ({
            ...previous,
            showBackButton: true,
            wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: false },
            innerViewState: ViewStateType.SELECT_PORTFOLIO,
            onBack: () => {
              setSlideOutConfig({
                ...previous,
                showBackButton: !!extraConfig?.initOnBack,
                wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: true },
                innerViewState: ViewStateType.DEFAULT,
                onBack: () => {
                  if (extraConfig?.initOnBack) return extraConfig?.initOnBack?.();
                },
              });
              feeModelInfo.resetSelectedPortfolio();
            },
          }));
        },
      }));

      return;
    }
    if (nextView === ViewStateType.SELECT_PORTFOLIO) {
      setSlideOutConfig((prev) => ({
        ...prev,
        showBackButton: true,
        wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: false },
        innerViewState: ViewStateType.SELECT_PORTFOLIO,
        onBack: () => {
          setSlideOutConfig((previous) => ({
            ...previous,
            showBackButton: !!extraConfig?.initOnBack,
            wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: true },
            innerViewState: ViewStateType.DEFAULT,
            onBack: () => {
              if (extraConfig?.initOnBack) return extraConfig?.initOnBack?.();
            },
          }));
          feeModelInfo.resetSelectedPortfolio();
        },
      }));
    }
  };

  const onStepNext = () => {
    const purchasePrice = (slideOutConfig.offer?.priceInTargetCurrency || 0) * quantitySelected;
    const _topupAmount = purchasePrice - (accountInfo?.account.totalCashOnAccount ?? 0);
    const params = generateBody(slideOutConfig, quantitySelected, availableMarketOffers.offers);
    setPurchaseInfo(params);
    if (!feeModelInfo.selectedPortfolio) {
      configBackButton(ViewStateType.SELECT_PORTFOLIO);
      return;
    }

    if (_topupAmount > 0) {
      if (!feeModelInfo.canTopUp) {
        // select portfolio
        configBackButton(ViewStateType.SELECT_PORTFOLIO);
        return;
      }

      // top up
      setTopAmount(_topupAmount / (fxRate ?? 1));
      configBackButton(ViewStateType.TOP_UP);
      return;
    }

    executeBuy({ body: params.body });
  };

  useEffect(() => {
    if (status === StatusTypes.isLoading) {
      setSlideOutConfig((prev) => ({
        ...prev,
        wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: false },
        showBackButton: false,
        view: ViewStateType.LOADING,
      }));
    }

    if (status === StatusTypes.success) {
      setSlideOutConfig((prev) => ({
        ...prev,
        wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: false },
        showBackButton: false,
        view: ViewStateType.CAMPAIGN_PURCHASE,
        onCTA: () => {
          nav(`${NavigationPath.MY_PORTFOLIO}#find=${purchaseInfoRef}`);
          setSlideOutConfig({ isOpen: false });
          extraConfig?.success?.cta?.();
        },
      }));
      extraConfig?.success?.onSuccess?.();

      setTimeout(() => {
        // only works if they are separately called
        queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS] });
        queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS] });
        queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_STOCK] });

        if (slideOutConfig?.searchText) {
          queryClient.resetQueries({
            queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS_SEARCH, slideOutConfig.searchText],
          });
          queryClient.resetQueries({ queryKey: [QUERY_KEYS.FETCH_OFFERS_SEARCH, slideOutConfig.searchText] });
        }

        accountInfo?.account?.refetchCashBalances?.();
      }, QUERY_REFECH_TIMEOUT);
    }

    if (status === StatusTypes.error) {
      setSlideOutConfig((prev) => ({
        ...prev,
        wineInfoTemplateConfig: { ...prev.wineInfoTemplateConfig, showTemplate: false },
        showBackButton: false,
        view: ViewStateType.DEFAULT,
        innerViewState: ViewStateType.PURCASE_ERROR,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, setSlideOutConfig, nav, purchaseInfoRef, queryClient, accountInfo?.account, extraConfig]);

  const onStatusChange = (viewState: ViewStateType) => {
    setSlideOutConfig((prev) => ({ ...prev, view: viewState }));
  };

  return (
    <div className="flex bg-white dark:bg-slate-900 flex-col flex-1">
      <FeedbackTemplate
        viewState={slideOutConfig?.view || ViewStateType.DEFAULT}
        templateConfig={slideOutConfig.templateConfig}
        onCTA={slideOutConfig.onCTA}
        onClose={() => {
          setSlideOutConfig({ isOpen: false });
        }}
      >
        <WineInfoTemplate
          config={{
            ...slideOutConfig.wineInfoTemplateConfig,
            displayText: {
              leftDisplayText: DisplayTextKeys.NUMBER_OF_UNIT_AVAILABLE,
              rightDisplayText: DisplayTextKeys.LOWEST_OFFER,
            },
            marketValue: gbpToTargetCurrency.convert(availableMarketOffers.lowestOfferGbp),
            quantity: availableMarketOffers.quantity,
          }}
        >
          {slideOutConfig?.innerViewState === ViewStateType.PURCASE_ERROR && (
            <RequestWineOnFailedPurchase
              offer={{
                expiryDate: '',
                price: slideOutConfig?.offer?.priceInTargetCurrency || 0,
                subtitle: `${slideOutConfig?.offer?.vintage} @ ${formatter.format(
                  slideOutConfig?.offer?.priceGbp || 0,
                )} per ${slideOutConfig.offer?.unitSize}`,
                name: `${slideOutConfig.offer?.wineName}`,
              }}
              resetStatus={resetStatus}
              onStatusChange={onStatusChange}
              qtyBought={quantitySelected}
              onClose={() => setSlideOutConfig({ isOpen: false })}
            />
          )}

          {slideOutConfig?.innerViewState === ViewStateType.TOP_UP && (
            <TopUpView
              fxRate={fxRate}
              topupAmount={topupAmount}
              portfolioId={Number(feeModelInfo.selectedPortfolio?.value) || -1}
              onSuccessUrl={`#${onTradeTopUpEnum.onTradeToupSuccess}=${purchaseInfoRef}`}
              onFailureUrl={`#${onTradeTopUpEnum.onTradeToupFailure}=${purchaseInfoRef}`}
            />
          )}

          {slideOutConfig?.innerViewState === ViewStateType.SELECT_PORTFOLIO && (
            <SelectPortfolioView
              feeModelInfo={feeModelInfo}
              onClick={() => {
                onStepNext();
              }}
            />
          )}
          {slideOutConfig?.innerViewState === ViewStateType.DEFAULT && (
            <>
              {/* Quantity Selection Card */}
              <div className="px-4 pt-2">
                <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4 border border-gray-100 dark:border-slate-700/50 shadow-sm dark:shadow-slate-950/30">
                  {isLoadingMarketOffers && (
                    <div className="flex flex-col gap-3">
                      <div className="h-4 rounded bg-gray-200 dark:bg-slate-700 animate-pulse w-3/4" />
                      <div className="h-12 rounded-xl bg-gray-200 dark:bg-slate-700 animate-pulse" />
                    </div>
                  )}

                  {!isLoadingMarketOffers && (
                    <div className="flex flex-col gap-3">
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                        {displayText[DisplayTextKeys.SELECT_QUANTITY_LABEL]}
                      </span>
                      <Dropdown
                        placeholder={displayText[DisplayTextKeys.PLEASE_SELECT_QUANTITY]}
                        value={quantitySelected > 0 ? quantitySelected?.toString() : ''}
                        containerClassName="w-full rounded-xl"
                        itemsContainerClassName="max-h-[250px] min-h-10 overflow-y-auto bg-white dark:bg-slate-700 rounded-xl shadow-lg dark:shadow-slate-950/50 border border-gray-200 dark:border-slate-600"
                        className="flex-1 text-sm text-gray-900 dark:text-white border-2 border-gray-200 dark:border-slate-600 h-12 p-4 rounded-xl justify-between items-center bg-white dark:bg-slate-700 hover:border-orange/50 dark:hover:border-teal-500/50 transition-colors"
                        itemsWrapperClassName="w-full"
                        items={generateQuantityDropdown(availableMarketOffers.quantity)}
                        onItemSelect={(item) => setQuantitySelected(Number(item.value))}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Balance Card */}
              <div className="px-4 py-3">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-800/50 rounded-xl p-3 border border-gray-100 dark:border-slate-700/50 shadow-sm dark:shadow-slate-950/30">
                  <div className="flex justify-between divide-x divide-gray-200 dark:divide-slate-700">
                    <div className="flex-1 flex flex-col items-center pr-3">
                      <span className="text-[10px] text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-0.5">
                        {t('product:wineDetails.investMore.currentBalance')}
                      </span>
                      <span className="text-sm text-gray-900 dark:text-white font-semibold">
                        {formatter.format(accountInfo?.account?.totalCashOnAccount as number, true)}
                      </span>
                    </div>

                    <div className="flex-1 flex flex-col items-center pl-3">
                      <span className="text-[10px] text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-0.5">
                        {t('product:wineDetails.investMore.balanceRemaining')}
                      </span>
                      <span className="text-sm text-gray-900 dark:text-white font-semibold">
                        {formatter.format(feeModelInfo?.getRemaingBalAndBtnText().remainingBal as number, true)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="px-4 pb-4 mt-auto">
                {isLoadingMarketOffers && (
                  <div className="h-12 rounded-full bg-gray-200 dark:bg-slate-700 animate-pulse" />
                )}
                {!isLoadingMarketOffers && (
                  <Button
                    isDisable={!(quantitySelected > 0) || status === StatusTypes.isLoading}
                    onClick={() => {
                      onStepNext();
                    }}
                    type="button"
                    className="w-full h-12 text-base font-semibold rounded-full btn bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/30 dark:shadow-teal-500/30 hover:shadow-xl hover:shadow-orange/40 dark:hover:shadow-teal-500/40 disabled:opacity-50 disabled:shadow-none transition-all"
                  >
                    {feeModelInfo.getRemaingBalAndBtnText().btnText}
                  </Button>
                )}
              </div>
            </>
          )}
        </WineInfoTemplate>
      </FeedbackTemplate>
    </div>
  );
};

export default PurchaseView;
